import { useLayoutEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetSiteIdentity } from '@/application/services/settings.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { getThemeById, envDefaultTheme } from '@/themes'
import { resolveThemeId } from '@/themes/resolve'
import {
  getPublicThemeOverride,
  subscribeToPublicThemeOverride,
} from '@/themes/publicThemeOverride'

const SUPPORTED_LANG_CODES = ['fr', 'en', 'es']

function resolveLanguage(input: string): string {
  const code = input.substring(0, 2).toLowerCase()
  return SUPPORTED_LANG_CODES.includes(code) ? code : 'fr'
}

const THEME_STYLE_ID = 'app-theme-styles'

function getOrCreateStyleElement(): HTMLStyleElement {
  const existing = document.getElementById(THEME_STYLE_ID)
  if (existing instanceof HTMLStyleElement) {
    return existing
  }
  const created = document.createElement('style')
  created.id = THEME_STYLE_ID
  document.head.appendChild(created)
  return created
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const { data: settings } = useGetSiteIdentity(resolveLanguage(i18n.language))
  const { isAuthenticated } = useAuthStore()
  // The Navbar's public light/dark toggle writes to localStorage and fires a
  // same-tab event (see publicThemeOverride.ts) — this local state re-render
  // is what makes the theme switch apply immediately on click, the same way
  // an auth-state change already does via the isAuthenticated dependency.
  const [publicThemeOverride, setPublicThemeOverrideState] = useState(getPublicThemeOverride)

  useLayoutEffect(
    () =>
      subscribeToPublicThemeOverride(() => setPublicThemeOverrideState(getPublicThemeOverride())),
    [],
  )

  const activeTheme = resolveThemeId(
    isAuthenticated,
    settings,
    envDefaultTheme,
    publicThemeOverride,
  )

  useLayoutEffect(() => {
    const theme = getThemeById(activeTheme)
    getOrCreateStyleElement().textContent = theme.css
  }, [activeTheme, isAuthenticated])

  return <>{children}</>
}
